import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

import { UNASSIGNED_GROUP_ID } from '../../pages/Create/components/Board/helpers/helpers'
import type { Idea } from '../../pages/Create/types'

export const LEGACY_UPDATE_IDEA = graphql(/* GraphQL */ `
  mutation LegacyUpdateIdea($input: UpdateIdeaInput!) {
    updateIdea(input: $input) {
      __typename
      ... on IdeaResponse {
        idea {
          id
          content {
            title
            text
            aiAssisted
            media {
              id
              url
              alt
              thumbnailUrl
              type
              size
              source {
                name
                id
              }
            }
            tags {
              id
              name
              color
            }
          }
          organizationId
          groupId
          position
          createdAt
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

export type LegacyUpdateIdeaMutation = ResultOf<typeof LEGACY_UPDATE_IDEA>
type LegacyUpdateIdeaMutationVariables = VariablesOf<typeof LEGACY_UPDATE_IDEA>
type IdeaUpdateInput = LegacyUpdateIdeaMutationVariables['input']

// HACK: this is a temporary solution to avoid sending the whole tag object to the server
// because types are not complete, data is spread and in some cases __typename is sent
// which is not allowed by the server, hence mutation fails
const transformIdeaForMutation = (
  idea: Idea,
  source?: string,
): IdeaUpdateInput => {
  return {
    id: idea.id,
    content: {
      ...idea.content,
      date: undefined,
      services: undefined,
      tags: idea.content.tags?.map((tag) => ({
        id: tag.id,
        name: tag.name,
        color: tag.color,
      })),
    },
    groupId: idea.groupId === UNASSIGNED_GROUP_ID ? null : idea.groupId,
    cta: source,
  }
}

type UpdateIdeaCompleted = NonNullable<
  useMutation.Options<
    LegacyUpdateIdeaMutation,
    LegacyUpdateIdeaMutationVariables
  >['onCompleted']
>

type EditIdeaError = NonNullable<
  useMutation.Options<
    LegacyUpdateIdeaMutation,
    LegacyUpdateIdeaMutationVariables
  >['onError']
>

export const useEditIdea = ({
  onCompleted,
  onError,
}: {
  onCompleted?: UpdateIdeaCompleted
  onError?: EditIdeaError
} = {}): {
  updateIdea: (idea: Idea, options?: { source?: string }) => void
  updateIdeaData?: LegacyUpdateIdeaMutation | null
  updateIdeaError?: ErrorLike
  updateIdeaLoading: boolean
} => {
  const [
    executeUpdateIdea,
    {
      data: updateIdeaData,
      error: updateIdeaError,
      loading: updateIdeaLoading,
    },
  ] = useMutation(LEGACY_UPDATE_IDEA, {
    onCompleted,
    onError,
  })

  const updateIdea = useCallback(
    (idea: Idea, options?: { source?: string }) => {
      executeUpdateIdea({
        variables: {
          input: transformIdeaForMutation(idea, options?.source),
        },
      })
    },
    [executeUpdateIdea],
  )

  return { updateIdea, updateIdeaData, updateIdeaError, updateIdeaLoading }
}
